import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  TablePagination,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  CheckBox as CheckBoxIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
  SelectAll as SelectAllIcon,
} from '@mui/icons-material';
import api from '../services/api';
import MathText from '../components/MathText';

interface Question {
  id: number;
  question: string;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  correct_option: string | null;
  correct_options: string[] | string;
  correct_options_count: number;
  question_type: 'single' | 'multiple';
  difficulty_level: string;
  marks: number;
  status: string;
  board_name: string;
  standard_name: string;
  medium_name?: string;
  subject_name: string;
  chapter_name: string;
  topic_name: string;
  created_at: string;
}

interface Stats {
  total_questions: number;
  draft_count: number;
  pending_review_count: number;
  approved_count: number;
  rejected_count: number;
}

interface Board { id: number; name: string; }
interface Standard { id: number; name: string; }
interface Subject { id: number; name: string; }
interface Chapter { id: number; name: string; }
interface Topic { id: number; name: string; }
interface Stream { id: number; name: string; }
interface Medium { id: number; code: string; name: string; native_name: string; }

type ActiveCard = 'all' | 'pending_review' | 'approved' | 'rejected' | 'draft' | null;

const ReviewQueue: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingAllIds, setLoadingAllIds] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [page, setPage] = useState(0); // 0-indexed for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [activeCard, setActiveCard] = useState<ActiveCard>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Master data for dropdowns
  const [boards, setBoards] = useState<Board[]>([]);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [mediums, setMediums] = useState<Medium[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);

  // Individual filter states (matching Questions.tsx pattern)
  const [boardFilter, setBoardFilter] = useState('');
  const [mediumFilter, setMediumFilter] = useState('');
  const [standardFilter, setStandardFilter] = useState('');
  const [streamFilter, setStreamFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [chapterFilter, setChapterFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog states
  const [viewDialog, setViewDialog] = useState<Question | null>(null);
  const [rejectDialog, setRejectDialog] = useState<Question | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.questions.getVerificationStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Fetch review queue
  const fetchReviewQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (boardFilter) params.board_id = boardFilter;
      if (mediumFilter) params.medium_id = mediumFilter;
      if (standardFilter) params.standard_id = standardFilter;
      if (subjectFilter) params.subject_id = subjectFilter;
      if (chapterFilter) params.chapter_id = chapterFilter;
      if (topicFilter) params.topic_id = topicFilter;
      if (difficultyFilter) params.difficulty_level = difficultyFilter;
      if (searchQuery) params.search = searchQuery;

      // Status: activeCard takes priority, then statusFilter dropdown
      if (activeCard) {
        params.status = activeCard;
      } else if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await api.questions.getReviewQueue(params);
      setQuestions(response.data.questions);
      setTotalCount(response.data.pagination?.total || 0);
      setSelectedQuestions([]);
      setSelectAllAcrossPages(false);
    } catch (error) {
      console.error('Failed to fetch review queue:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, boardFilter, mediumFilter, standardFilter, subjectFilter, chapterFilter, topicFilter, difficultyFilter, statusFilter, activeCard]);

  // ---------- Master data loaders (same as Questions.tsx) ----------

  const loadMasterData = async () => {
    try {
      const [boardsRes] = await Promise.all([api.master.getBoards()]);
      setBoards(boardsRes.data.boards || []);
    } catch (err) {
      console.error('Failed to load master data:', err);
    }
  };

  const loadMediums = async () => {
    try {
      const response = await api.master.getMediums();
      setMediums(response.data.mediums || []);
    } catch (err) {
      console.error('Failed to load mediums:', err);
    }
  };

  const loadStandards = async (boardId: string) => {
    if (!boardId) { setStandards([]); return; }
    try {
      const response = await api.master.getStandards(parseInt(boardId));
      setStandards(response.data.standards || []);
    } catch (err) {
      console.error('Failed to load standards:', err);
    }
  };

  const loadStreams = async (standardId: string) => {
    if (!standardId) { setStreams([]); return; }
    try {
      const response = await api.master.getStreams(parseInt(standardId));
      setStreams(response.data.streams || []);
    } catch (err) {
      console.error('Failed to load streams:', err);
    }
  };

  const loadSubjects = async (boardId: string, standardId: string, mediumId?: string) => {
    if (!boardId || !standardId) { setSubjects([]); return; }
    try {
      const response = await api.master.getSubjects(
        parseInt(boardId),
        parseInt(standardId),
        mediumId ? parseInt(mediumId) : undefined
      );
      setSubjects(response.data.subjects || []);
    } catch (err) {
      console.error('Failed to load subjects:', err);
    }
  };

  const loadChapters = async (subjectId: string) => {
    if (!subjectId) { setChapters([]); return; }
    try {
      const response = await api.master.getChapters(parseInt(subjectId));
      setChapters(response.data.chapters || []);
    } catch (err) {
      console.error('Failed to load chapters:', err);
    }
  };

  const loadTopics = async (chapterId: string) => {
    if (!chapterId) { setTopics([]); return; }
    try {
      const response = await api.master.getTopics(parseInt(chapterId));
      setTopics(response.data.topics || []);
    } catch (err) {
      console.error('Failed to load topics:', err);
    }
  };

  // ---------- Handler functions for dropdown changes ----------

  const handleBoardChange = (value: string) => {
    setBoardFilter(value);
    setMediumFilter('');
    setStandardFilter('');
    setStreamFilter('');
    setSubjectFilter('');
    setChapterFilter('');
    setTopicFilter('');
    setMediums([]);
    setStandards([]);
    setSubjects([]);
    setChapters([]);
    setTopics([]);
    setPage(0);
    if (value) {
      loadMediums();
      loadStandards(value);
    }
  };

  const handleMediumChange = (value: string) => {
    setMediumFilter(value);
    setSubjectFilter('');
    setChapterFilter('');
    setTopicFilter('');
    setSubjects([]);
    setChapters([]);
    setTopics([]);
    setPage(0);
    if (boardFilter && standardFilter) {
      loadSubjects(boardFilter, standardFilter, value);
    }
  };

  const handleStandardChange = (value: string) => {
    setStandardFilter(value);
    setStreamFilter('');
    setSubjectFilter('');
    setChapterFilter('');
    setTopicFilter('');
    setSubjects([]);
    setChapters([]);
    setTopics([]);
    setPage(0);
    loadStreams(value);
    if (boardFilter) {
      loadSubjects(boardFilter, value, mediumFilter);
    }
  };

  const handleSubjectChange = (value: string) => {
    setSubjectFilter(value);
    setChapterFilter('');
    setTopicFilter('');
    setPage(0);
    loadChapters(value);
  };

  const handleChapterChange = (value: string) => {
    setChapterFilter(value);
    setTopicFilter('');
    setPage(0);
    loadTopics(value);
  };

  // Handle card click
  const handleCardClick = (card: ActiveCard) => {
    setActiveCard((prev) => (prev === card ? null : card));
    setStatusFilter(''); // Clear status dropdown when using cards
    setPage(0);
  };

  // Handle search
  const handleSearch = () => {
    setPage(0);
    fetchReviewQueue();
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // ---------- Initial load ----------
  useEffect(() => {
    fetchStats();
    loadMasterData();
  }, []);

  // Re-fetch when filters or pagination change
  useEffect(() => {
    fetchReviewQueue();
  }, [fetchReviewQueue]);

  // ---------- Action handlers ----------

  const handleApprove = async (questionId: number) => {
    try {
      await api.questions.approve(questionId);
      fetchReviewQueue();
      fetchStats();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to approve question');
    }
  };

  const handleReject = async () => {
    if (!rejectDialog || !rejectReason.trim()) {
      alert('Rejection reason is required');
      return;
    }
    try {
      await api.questions.reject(rejectDialog.id, rejectReason);
      setRejectDialog(null);
      setRejectReason('');
      fetchReviewQueue();
      fetchStats();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to reject question');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedQuestions.length === 0) {
      setSnackbar({ open: true, message: 'Please select questions to approve', severity: 'info' });
      return;
    }
    const count = selectAllAcrossPages ? totalCount : selectedQuestions.length;
    if (!window.confirm(`Are you sure you want to approve ${count} question(s)?`)) return;

    setBulkApproving(true);
    try {
      const response = await api.questions.bulkApprove(selectedQuestions);
      const approvedCount = response.data?.approved_count || selectedQuestions.length;
      setSnackbar({
        open: true,
        message: `✅ Successfully approved ${approvedCount} question(s)`,
        severity: 'success',
      });
      fetchReviewQueue();
      fetchStats();
      setSelectedQuestions([]);
      setSelectAllAcrossPages(false);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to bulk approve questions',
        severity: 'error',
      });
    } finally {
      setBulkApproving(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedQuestions([]);
    setSelectAllAcrossPages(false);
  };

  const toggleSelection = (questionId: number) => {
    setSelectAllAcrossPages(false);
    setSelectedQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedQuestions.length === questions.length) {
      setSelectedQuestions([]);
      setSelectAllAcrossPages(false);
    } else {
      setSelectedQuestions(questions.map((q) => q.id));
    }
  };

  const handleSelectAllAcrossPages = async () => {
    setSelectAllAcrossPages(true);
    setLoadingAllIds(true);
    try {
      const params: any = {};
      if (boardFilter) params.board_id = boardFilter;
      if (standardFilter) params.standard_id = standardFilter;
      if (subjectFilter) params.subject_id = subjectFilter;
      if (mediumFilter) params.medium_id = mediumFilter;

      const response = await api.questions.getAllPendingIds(params);
      setSelectedQuestions(response.data.ids);
    } catch (error) {
      setSelectAllAcrossPages(false);
      alert('Failed to select all questions');
    } finally {
      setLoadingAllIds(false);
    }
  };

  const getCorrectAnswerDisplay = (question: Question) => {
    if (question.question_type === 'multiple') {
      const options = typeof question.correct_options === 'string'
        ? JSON.parse(question.correct_options)
        : question.correct_options;
      return options.join(', ');
    }
    return question.correct_option || 'N/A';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending_review': return 'warning';
      case 'rejected': return 'error';
      case 'draft': return 'info';
      default: return 'default';
    }
  };

  const getListLabel = () => {
    switch (activeCard) {
      case 'all': return 'All Questions';
      case 'pending_review': return 'Pending Review Questions';
      case 'approved': return 'Approved Questions';
      case 'rejected': return 'Rejected Questions';
      case 'draft': return 'Draft Questions';
      default: return 'Pending Questions';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Review Queue
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review and manage question verification status
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 calc(20% - 16px)', minWidth: '200px' }}>
            <Card
              onClick={() => handleCardClick('all')}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: activeCard === 'all' ? '2px solid' : '2px solid transparent',
                borderColor: activeCard === 'all' ? 'primary.main' : 'transparent',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              }}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Questions</Typography>
                <Typography variant="h4">{stats.total_questions}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 calc(20% - 16px)', minWidth: '200px' }}>
            <Card
              onClick={() => handleCardClick('pending_review')}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: activeCard === 'pending_review' ? '2px solid' : '2px solid transparent',
                borderColor: activeCard === 'pending_review' ? 'warning.main' : 'transparent',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              }}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Pending Review</Typography>
                <Typography variant="h4" color="warning.main">{stats.pending_review_count}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 calc(20% - 16px)', minWidth: '200px' }}>
            <Card
              onClick={() => handleCardClick('approved')}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: activeCard === 'approved' ? '2px solid' : '2px solid transparent',
                borderColor: activeCard === 'approved' ? 'success.main' : 'transparent',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              }}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Approved</Typography>
                <Typography variant="h4" color="success.main">{stats.approved_count}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 calc(20% - 16px)', minWidth: '200px' }}>
            <Card
              onClick={() => handleCardClick('rejected')}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: activeCard === 'rejected' ? '2px solid' : '2px solid transparent',
                borderColor: activeCard === 'rejected' ? 'error.main' : 'transparent',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              }}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Rejected</Typography>
                <Typography variant="h4" color="error.main">{stats.rejected_count}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 calc(20% - 16px)', minWidth: '200px' }}>
            <Card
              onClick={() => handleCardClick('draft')}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: activeCard === 'draft' ? '2px solid' : '2px solid transparent',
                borderColor: activeCard === 'draft' ? 'info.main' : 'transparent',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              }}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Draft</Typography>
                <Typography variant="h4" color="info.main">{stats.draft_count}</Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Filters — matching Questions.tsx pattern */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Board</InputLabel>
            <Select
              value={boardFilter}
              label="Board"
              onChange={(e) => handleBoardChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {boards.map((board) => (
                <MenuItem key={board.id} value={board.id}>{board.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!boardFilter}>
            <InputLabel>Medium</InputLabel>
            <Select
              value={mediumFilter}
              label="Medium"
              onChange={(e) => handleMediumChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {mediums.map((medium) => (
                <MenuItem key={medium.id} value={medium.id}>{medium.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!boardFilter}>
            <InputLabel>Standard</InputLabel>
            <Select
              value={standardFilter}
              label="Standard"
              onChange={(e) => handleStandardChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {standards.map((standard) => (
                <MenuItem key={standard.id} value={standard.id}>{standard.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!standardFilter}>
            <InputLabel>Stream</InputLabel>
            <Select
              value={streamFilter}
              label="Stream"
              onChange={(e) => setStreamFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {streams.map((stream) => (
                <MenuItem key={stream.id} value={stream.id}>{stream.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!standardFilter}>
            <InputLabel>Subject</InputLabel>
            <Select
              value={subjectFilter}
              label="Subject"
              onChange={(e) => handleSubjectChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {subjects.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>{subject.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!subjectFilter}>
            <InputLabel>Chapter</InputLabel>
            <Select
              value={chapterFilter}
              label="Chapter"
              onChange={(e) => handleChapterChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {chapters.map((chapter) => (
                <MenuItem key={chapter.id} value={chapter.id}>{chapter.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }} disabled={!chapterFilter}>
            <InputLabel>Topic</InputLabel>
            <Select
              value={topicFilter}
              label="Topic"
              onChange={(e) => { setTopicFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {topics.map((topic) => (
                <MenuItem key={topic.id} value={topic.id}>{topic.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Difficulty</InputLabel>
            <Select
              value={difficultyFilter}
              label="Difficulty"
              onChange={(e) => { setDifficultyFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="easy">Easy</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="hard">Hard</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeCard ? '' : statusFilter}
              label="Status"
              disabled={!!activeCard}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending_review">Pending Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            sx={{ flexGrow: 1 }}
          />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
            Search
          </Button>
        </Box>
      </Paper>

      {/* Bulk Actions Banner */}
      {selectedQuestions.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            mb: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: selectAllAcrossPages ? 'success.main' : 'primary.main',
            borderRadius: 2,
          }}
        >
          {/* Progress bar during bulk approve */}
          {bulkApproving && <LinearProgress color="success" />}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 1.5,
              background: selectAllAcrossPages
                ? 'linear-gradient(135deg, rgba(46,125,50,0.08) 0%, rgba(46,125,50,0.04) 100%)'
                : 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.04) 100%)',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {/* Left side: selection info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                icon={selectAllAcrossPages ? <DoneAllIcon /> : <CheckBoxIcon />}
                label={
                  selectAllAcrossPages
                    ? `All ${totalCount} questions selected`
                    : `${selectedQuestions.length} selected`
                }
                color={selectAllAcrossPages ? 'success' : 'primary'}
                variant="filled"
                sx={{ fontWeight: 600, fontSize: '0.875rem' }}
              />

              {/* Show "Select all across pages" link when current page is fully selected */}
              {!selectAllAcrossPages && selectedQuestions.length === questions.length && totalCount > questions.length && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={loadingAllIds ? <CircularProgress size={14} /> : <SelectAllIcon />}
                  onClick={handleSelectAllAcrossPages}
                  disabled={loadingAllIds}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'primary.main',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {loadingAllIds ? 'Loading...' : `Select all ${totalCount} matching questions`}
                </Button>
              )}

              {/* Confirmation message when all across pages are selected */}
              {selectAllAcrossPages && (
                <Typography variant="body2" color="success.dark" sx={{ fontWeight: 500 }}>
                  All questions matching current filters are selected across all pages.
                </Typography>
              )}
            </Box>

            {/* Right side: action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={handleBulkApprove}
                disabled={bulkApproving || loadingAllIds}
                startIcon={
                  bulkApproving
                    ? <CircularProgress size={16} color="inherit" />
                    : <ApproveIcon />
                }
                sx={{
                  fontWeight: 600,
                  px: 2.5,
                  boxShadow: 2,
                  '&:hover': { boxShadow: 4 },
                }}
              >
                {bulkApproving
                  ? 'Approving...'
                  : `Approve ${selectAllAcrossPages ? totalCount : selectedQuestions.length} Question(s)`
                }
              </Button>
              <Tooltip title="Clear selection">
                <IconButton
                  size="small"
                  onClick={handleClearSelection}
                  sx={{ color: 'text.secondary' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Questions List */}
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {getListLabel()} ({totalCount})
          </Typography>
          <Box>
            <Button
              startIcon={<CheckBoxIcon />}
              onClick={handleSelectAll}
              disabled={questions.length === 0}
            >
              {selectedQuestions.length === questions.length && questions.length > 0 ? 'Deselect All' : 'Select All'}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : questions.length === 0 ? (
          <Alert severity="info">No questions found{activeCard ? ` with status "${activeCard === 'all' ? 'any' : activeCard}"` : ' pending review'}</Alert>
        ) : (
          <>
            {questions.map((question) => (
              <Card key={question.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Checkbox
                      checked={selectedQuestions.includes(question.id)}
                      onChange={() => toggleSelection(question.id)}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Chip label={question.board_name} size="small" color="primary" />
                        <Chip label={question.standard_name} size="small" />
                        {question.medium_name && <Chip label={question.medium_name} size="small" variant="outlined" />}
                        <Chip label={question.subject_name} size="small" />
                        <Chip label={question.chapter_name} size="small" />
                        <Chip label={question.difficulty_level} size="small" color="secondary" />
                        <Chip
                          label={question.question_type === 'multiple' ? 'Multiple Choice' : 'Single Choice'}
                          size="small"
                          color="info"
                        />
                        <Chip label={`${question.marks} marks`} size="small" variant="outlined" />
                        {(activeCard === 'all' || statusFilter || activeCard !== 'pending_review') && (
                          <Chip
                            label={question.status}
                            size="small"
                            color={getStatusColor(question.status) as any}
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                        <MathText text={question.question} />
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ flex: '1 1 45%' }}>
                          <Typography variant="body2" color="textSecondary">
                            A) <MathText text={question.option1} />
                          </Typography>
                        </Box>
                        <Box sx={{ flex: '1 1 45%' }}>
                          <Typography variant="body2" color="textSecondary">
                            B) <MathText text={question.option2} />
                          </Typography>
                        </Box>
                        <Box sx={{ flex: '1 1 45%' }}>
                          <Typography variant="body2" color="textSecondary">
                            C) <MathText text={question.option3} />
                          </Typography>
                        </Box>
                        <Box sx={{ flex: '1 1 45%' }}>
                          <Typography variant="body2" color="textSecondary">
                            D) <MathText text={question.option4} />
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="success.main" sx={{ mt: 1, fontWeight: 'bold' }}>
                        Correct Answer: {getCorrectAnswerDisplay(question)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton color="info" onClick={() => setViewDialog(question)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Approve">
                        <IconButton color="success" onClick={() => handleApprove(question.id)}>
                          <ApproveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton color="error" onClick={() => setRejectDialog(question)}>
                          <RejectIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {/* Pagination — MUI TablePagination (same as Questions.tsx) */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Question Details</DialogTitle>
        <DialogContent>
          {viewDialog && (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={viewDialog.board_name} color="primary" />
                <Chip label={viewDialog.standard_name} />
                <Chip label={viewDialog.subject_name} />
                <Chip label={viewDialog.chapter_name} />
                <Chip label={viewDialog.topic_name} />
                <Chip label={viewDialog.difficulty_level} color="secondary" />
                <Chip
                  label={viewDialog.question_type === 'multiple' ? 'Multiple Choice' : 'Single Choice'}
                  color="info"
                />
              </Box>
              <Typography variant="h6" gutterBottom>Question:</Typography>
              <Typography variant="body1" paragraph>
                <MathText text={viewDialog.question} />
              </Typography>
              <Typography variant="h6" gutterBottom>Options:</Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body1">A) <MathText text={viewDialog.option1} /></Typography>
                <Typography variant="body1">B) <MathText text={viewDialog.option2} /></Typography>
                <Typography variant="body1">C) <MathText text={viewDialog.option3} /></Typography>
                <Typography variant="body1">D) <MathText text={viewDialog.option4} /></Typography>
              </Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Correct Answer:</Typography>
              <Typography variant="body1" color="success.main" sx={{ fontWeight: 'bold' }}>
                {getCorrectAnswerDisplay(viewDialog)}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Marks: {viewDialog.marks} | Created: {new Date(viewDialog.created_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(null)}>Close</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<ApproveIcon />}
            onClick={() => {
              if (viewDialog) { handleApprove(viewDialog.id); setViewDialog(null); }
            }}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<RejectIcon />}
            onClick={() => {
              if (viewDialog) { setRejectDialog(viewDialog); setViewDialog(null); }
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Question</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this question:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason *"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g., Incorrect answer, unclear question, formatting issues..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectDialog(null); setRejectReason(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={!rejectReason.trim()}
          >
            Reject Question
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewQueue;
